import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  useGetCreatedFertsQuery,
  useGetFertCommentsQuery,
  useGetFertilizationDonorSchedulesQuery,
  useGetFertilizationPulledSemenQuery,
  useGetFertTechsQuery,
  useGetInlineFertCommentsQuery,
  useGetTravelingTechsQuery,
  useCreateFertilizationMutation as useGqlCreateFertilizationMutation,
  useUpsertReverseSortMutation as useGqlUpsertReverseSortMutation,
  useLazyGetFertilizationScheduledDonorsQuery,
} from '@/app/services/fertilizationApp';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';
import {
  CommentItem,
  CreatedFertItem,
  createdFertsOptions,
  CreateFertilizationRequest,
  CreateFertilizationResponse,
  fertCommentsInGridOptions,
  fertCommentsOptions,
  FertDonorScheduleItem,
  fertDonorSchedulesOptions,
  FertPulledSemenItem,
  fertPulledSemenOptions,
  FertScheduledDonorItem,
  fertScheduledDonorsOptions,
  fertTechsOptions,
  TechItem,
  UpsertReverseSortRequest,
  useCreateFertilizationRestMutation,
  useCreateFertilizationV2RestMutation,
  useUpsertReverseSortRestMutation,
} from '@/services/fertilization';

const FLAG = '29386_fertilization_graphql_to_rest';
const INACTIVE_SIRE_FLAG = 'block_inactive_sires';

export function useCreatedFertsQuery(params: {
  donorScheduleDate: string | null;
  fertSiteId: string | null;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorScheduleDate && !!params.fertSiteId;

  const restQuery = useQuery({
    ...createdFertsOptions(token, {
      donorScheduleDate: params.donorScheduleDate!,
      fertSiteId: params.fertSiteId!,
    }),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useGetCreatedFertsQuery(
    {
      donorScheduleDate: params.donorScheduleDate as string,
      fertSiteId: params.fertSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    const mappedData = Array.isArray(restQuery.data)
      ? restQuery.data.map((item: any) => ({
          ...item,
          id: item.guidValue,
        }))
      : [];
    return {
      data: mappedData,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.createdFerts?.items as CreatedFertItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertPulledSemenQuery(params: {
  donorScheduleDate: string | null;
  fertSiteId: string | null;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorScheduleDate && !!params.fertSiteId;

  const restQuery = useQuery({
    ...fertPulledSemenOptions(token, {
      donorScheduleDate: params.donorScheduleDate!,
      fertSiteId: params.fertSiteId!,
    }),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useGetFertilizationPulledSemenQuery(
    {
      donorScheduleDate: params.donorScheduleDate as string,
      fertSiteId: params.fertSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.pulledSemen?.items as
      | FertPulledSemenItem[]
      | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertDonorSchedulesQuery(params: {
  donorScheduleDate: string | null;
  fertSiteId: string | null;
}) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorScheduleDate && !!params.fertSiteId;

  const restQuery = useQuery({
    ...fertDonorSchedulesOptions(token, {
      donorScheduleDate: params.donorScheduleDate!,
      fertSiteId: params.fertSiteId!,
    }),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useGetFertilizationDonorSchedulesQuery(
    {
      donorScheduleDate: params.donorScheduleDate as string,
      fertSiteId: params.fertSiteId as string,
    },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.fertilizationDonorSchedules?.items as
      | FertDonorScheduleItem[]
      | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useFertScheduledDonorsLazy() {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const queryClient = useQueryClient();
  const [gqlScheduledDonors] = useLazyGetFertilizationScheduledDonorsQuery();

  const fetchScheduledDonors = useCallback(
    async (params: {
      semenID: string;
      donorScheduleDate: string;
      semenType: string;
      accountID: string;
    }): Promise<FertScheduledDonorItem[]> => {
      if (isRest) {
        return queryClient.fetchQuery(
          fertScheduledDonorsOptions(token, {
            donorScheduleDate: params.donorScheduleDate,
            semenId: params.semenID,
            accountId: params.accountID,
            semenType: params.semenType,
          }),
        );
      }
      const result = await gqlScheduledDonors({
        semenID: params.semenID,
        donorScheduleDate: params.donorScheduleDate,
        semenType: params.semenType,
        accountID: params.accountID,
      }).unwrap();
      return (result?.scheduledDonors?.items ?? []) as FertScheduledDonorItem[];
    },
    [isRest, queryClient, gqlScheduledDonors, token],
  );

  return fetchScheduledDonors;
}

export function useFertTechsQuery(params: { fertSiteId: string | null }) {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.fertSiteId;

  const restQuery = useQuery({
    ...fertTechsOptions(token, { fertSiteId: params.fertSiteId }),
    enabled: !!token && isRest && enabled,
  });

  const gqlQuery = useGetFertTechsQuery(
    { fertSiteId: params.fertSiteId },
    { skip: isRest || !enabled },
  );

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.techs?.items as TechItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useTravelingTechsQuery() {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);

  const restQuery = useQuery({
    ...fertTechsOptions(token, {}),
    enabled: !!token && isRest,
  });

  const gqlQuery = useGetTravelingTechsQuery({}, { skip: isRest });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.techs?.items as TechItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useFertCommentsQuery() {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);

  const restQuery = useQuery({
    ...fertCommentsOptions(token),
    enabled: !!token && isRest,
  });

  const gqlQuery = useGetFertCommentsQuery({}, { skip: isRest });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.fertCommentsList?.items as CommentItem[] | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useInlineFertCommentsQuery() {
  const token = useAccessToken();
  const isRest = useFeatureFlag(FLAG);

  const restQuery = useQuery({
    ...fertCommentsInGridOptions(token),
    enabled: !!token && isRest,
  });

  const gqlQuery = useGetInlineFertCommentsQuery({}, { skip: isRest });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      error: restQuery.error,
    };
  }
  return {
    data: gqlQuery.data?.fertCommentsInGridList?.items as
      | CommentItem[]
      | undefined,
    isLoading: gqlQuery.isLoading,
    error: null,
  };
}

export function useCreateFertilization() {
  const isBlockInactiveSires = useFeatureFlag(INACTIVE_SIRE_FLAG);
  const isRest = useFeatureFlag(FLAG) || isBlockInactiveSires;
  const restMutation = useCreateFertilizationRestMutation();
  const restV2Mutation = useCreateFertilizationV2RestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useGqlCreateFertilizationMutation();

  const mutateAsync = useCallback(
    async (
      request: CreateFertilizationRequest,
    ): Promise<CreateFertilizationResponse | undefined> => {
      if (isRest) {
        const createMutation = isBlockInactiveSires
          ? restV2Mutation
          : restMutation;
        return createMutation.mutateAsync(request);
      }
      const result = await gqlMutate(request).unwrap();
      return result?.createFertilizations as
        | CreateFertilizationResponse
        | undefined;
    },
    [isRest, isBlockInactiveSires, restMutation, restV2Mutation, gqlMutate],
  );

  return {
    mutateAsync,
    isLoading: isRest
      ? restMutation.isPending || restV2Mutation.isPending
      : gqlLoading,
  };
}

export function useUpsertReverseSort() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpsertReverseSortRestMutation();
  const [gqlMutate, { isLoading: gqlLoading }] =
    useGqlUpsertReverseSortMutation();

  const mutateAsync = useCallback(
    async (request: UpsertReverseSortRequest) => {
      if (isRest) {
        return restMutation.mutateAsync(request);
      }
      return gqlMutate(request).unwrap();
    },
    [isRest, restMutation, gqlMutate],
  );

  return {
    mutateAsync,
    isLoading: isRest ? restMutation.isPending : gqlLoading,
  };
}
